import * as React from 'react';
import { ExclamationCircleIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Variable } from '@restworld/utility-types';
import { scrollToView, useModal } from '@restworld/utils-common';

import {
  CheckboxGroup,
  Heading,
  Button,
  RadioGroup,
  Modal,
  ModalContent,
  ModalConfirmContent,
} from '@restworld/ui-ds-base';
import { MediaFormContent } from './media-form-content';
import { PersonalInfoFormContent } from './personal-info-form-content';
import { TransportationFormContent } from './transportation-form-content';
import { PresentationFormContent } from './presentation-form-content';
import { WorkExperienceFormContent } from './experience-form-content';
import { LanguageFormContent } from './language-form-content';
import { EducationFormContent } from './education-form-content';
import { CertificationsFormContent } from './certification-form-content';
import { PreferencesFormContent } from './preferences-form-content';
import { ProfessionalAssociationsFormContent } from './professional-associations-form-content';

export const renderComponentObjectMapping = {
  personal_info: PersonalInfoFormContent,
  work_experience: WorkExperienceFormContent,
  transportation: TransportationFormContent,
  media: MediaFormContent,
  presentation: PresentationFormContent,
  languages: LanguageFormContent,
  education: EducationFormContent,
  preferences: PreferencesFormContent,
  certifications: CertificationsFormContent,
  professional_associations: ProfessionalAssociationsFormContent,
};
export interface IEditFormContentProps {
  editPageType?: keyof typeof renderComponentObjectMapping;
  editPageTitle?: string;
  showAlert?: boolean;
}
export interface IEditModalProps extends IEditFormContentProps {
  showModal?: boolean;
  onClose?: () => void;
  mode?: 'edit' | 'add';
  editIndex?: number;
}
export const ProfileEditModal: React.FunctionComponent<IEditModalProps> = ({ showModal = false, onClose, ...rest }) => {
  return <EditFormContent {...rest} showModal={showModal} onClose={onClose} />;
};
export default ProfileEditModal;
const EditFormContent = ({ editPageType, ...rest }: IEditModalProps) => {
  const RenderComponent = renderComponentObjectMapping[editPageType];
  return <>{editPageType ? <RenderComponent {...rest} /> : null}</>;
};
export const EditModalTitle = ({
  editPageTitle,
  onClose,
  onDelete,
  loading,
}: {
  editPageTitle?: IEditFormContentProps['editPageTitle'];
  onClose: () => void;
  onDelete?: () => void;
  loading?: boolean;
}) => {
  const { show, open, close } = useModal();
  const { t } = useTranslation();
  return (
    <>
      <div className="pb-4 !translate-y-0">
        <div className="flex justify-between">
          <div className="flex flex-row gap-4">
            {onDelete ? <Button StartIcon={TrashIcon} variant="secondary-danger" onClick={open} /> : null}
            <Heading className="leading-7.5">{editPageTitle}</Heading>
          </div>
          {onClose ? <Button StartIcon={XMarkIcon} variant="tertiary" onClick={() => onClose()} /> : null}
        </div>
      </div>
      <Modal open={show} onOpenChange={(v) => !v && close()}>
        <ModalContent variant="center" className="md:max-w-lg lg:max-w-2xl">
          <ModalConfirmContent
            title={t('onboarding-page:delete_experience_confirmation')}
            primaryAction={{
              label: t('common:continue'),
              onClick: onDelete,
              loading,
            }}
            secondaryAction={{
              label: t('common:cancel'),
              onClick: close,
            }}
            reversed
          >
            <ExclamationCircleIcon className="w-12 h-12 mx-auto text-red-500" />
          </ModalConfirmContent>
        </ModalContent>
      </Modal>
    </>
  );
};
export const EditModalFooter = ({
  loading,
  onClose,
  onSave,
  editPageType,
  editIndex,
}: {
  loading?: boolean;
  onClose?: () => void;
  onSave?: () => void;
  editPageType?: keyof typeof renderComponentObjectMapping;
  editIndex?: number;
}) => {
  const scrollIndex = `${editPageType}${editIndex > -1 ? `-${editIndex}` : ''}`;
  const { t } = useTranslation();
  return (
    <div className="flex flex-col md:flex-row-reverse gap-4 pb-4">
      <Button
        className={'md:w-1/2 w-full'}
        type="submit"
        loading={loading}
        onClick={async () => {
          if (onSave) onSave();
        }}
      >
        {t('common:save_changes')}
      </Button>
      <Button
        className={'md:w-1/2 w-full'}
        variant="secondary-neutral"
        onClick={() => {
          onClose();
          setTimeout(() => {
            scrollToView(scrollIndex);
          }, 300);
        }}
      >
        {t('common:quit_without_saving')}
      </Button>
    </div>
  );
};
export const EditFieldOptionsModal = ({
  id,
  options,
  mainTitle,
  value,
  onBack,
  onChange,
  onSave,
  visible,
  returnType,
}: {
  onBack: () => void;
  id: string;
  options: Variable[];
  mainTitle: string;
  onChange: (value: string | React.ChangeEvent<HTMLInputElement>) => void;
  value?: string | string[];
  onSave?: () => void;
  visible?: boolean;
  returnType?: FieldReturnType;
}) => {
  const { t } = useTranslation();
  return (
    <Modal open={visible} onOpenChange={(v) => !v && onBack()}>
      <ModalContent
        variant="center"
        className="md:max-w-md lg:max-w-md md:max-h-[calc(100vh-400px)]"
        footer={
          <Button fullwidth onClick={onSave}>
            {t('common:confirm')}
          </Button>
        }
        title={mainTitle}
      >
        <div className="flex flex-col gap-4 w-full md:max-h-[80vh]">
          <div className="self-center w-full ">
            {typeof value === 'string' && returnType === 'string' && (
              <RadioGroup
                key={mainTitle}
                id={id}
                options={options || []}
                value={value}
                onChange={onChange}
                className={'max-w-full w-full'}
                isFullWidth
              />
            )}
            {typeof value !== 'string' && returnType === 'arrayString' && (
              <CheckboxGroup
                id={id}
                key={mainTitle + id}
                options={options || []}
                mode="clickable"
                variant="box"
                isWrapList={false}
                onChange={onChange}
                value={value}
                background="primary"
                isEditable={true}
              />
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};
type FieldReturnType = 'string' | 'arrayString';
type FieldOptionsViewType = {
  showOptionsView: boolean;
  options: Variable[];
  mainTitle: string;
  subTitle?: string;
  selected?: string[] | string;
  returnType?: FieldReturnType;
  id: string; // Modified type of id
};
export type { FieldReturnType, FieldOptionsViewType };
